"use client"

import { useRef, useState, useCallback, useMemo } from "react"
import { useInfiniteScroll } from "../../hooks/useInfiniteScroll"
import { loadMoreCards } from "../server-actions"
import { CardAndPostArray, VerticalCardProps } from "../types"
import createDataSource from "../sources/factory"
import { loadedMoreFeed } from "../../tracking"

/**
 * VerticalCards is a client component that renders a list of posts in a vertical format.
 * It uses the useInfiniteScroll hook to detect user interaction and fetch more posts.
 */
export default function VerticalCards({
  initialPosts,
  type,
  title,
  description,
  data,
}: VerticalCardProps) {
  const dataSource = useMemo(
    () =>
      createDataSource({
        type,
        userId: data?.userId,
        walletAddress: data?.wallet_address,
      }),
    [type, data]
  )

  /**
   * These are the actual cards that will be rendered.
   */
  const [posts, setPosts] = useState<CardAndPostArray>(
    initialPosts?.cards || []
  )

  const [isLoading, setIsLoading] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [pagination, setPagination] = useState(
    initialPosts?.pagination || dataSource.getInitialPagination()
  )
  const contentRef = useRef<HTMLDivElement | null>(null)

  const loadMore = useCallback(async () => {
    if (isLoading || !hasMore) return

    loadedMoreFeed(type, pagination.page, data?.userId)

    setIsLoading(true)
    try {
      const result = await loadMoreCards({ pagination, type, data })

      if (result && result.cards.length > 0) {
        setPosts((prevPosts) => [...prevPosts, ...result.cards])
        setPagination(result.pagination)
      } else {
        setHasMore(false)
      }
    } catch (error) {
      console.error("Error loading more posts:", error)
      setHasMore(false)
    } finally {
      setIsLoading(false)
    }
  }, [posts, type, pagination, data, isLoading, hasMore])

  useInfiniteScroll(contentRef, loadMore, 0.8)

  return (
    <div className="overflow-visible">
      <div
        className="space-y-4 flex flex-col items-center ml-0 sm:ml-9"
        ref={contentRef}
      >
        {posts.map((entry, index) => (
          <div
            key={`additional-${JSON.stringify(pagination)}-${index}`}
            className=""
          >
            {entry.card}
          </div>
        ))}
        {isLoading && (
          <div className="flex justify-center items-center h-10 w-full max-w-[510px] mr-auto">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
          </div>
        )}
        {!hasMore && !isLoading && <div>No more posts to load.</div>}
      </div>
    </div>
  )
}
